import { Box, Button } from '@mui/material';
import { JSX } from 'react/jsx-runtime';
import {
  Edit,
  EditProps,
  ImageField,
  SaveButton,
  SimpleForm,
  Toolbar,
  ToolbarProps,
  useCreatePath,
  useRecordContext,
  useRedirect,
} from 'react-admin';

import cleanNewRecord from '@/Components/CleanNewRecord';
import ClearError from '@/Components/ClearError';
import AlbumArtistSection from '@/Album/AlbumArtistSection';
import AlbumContributorsSection from '@/Album/AlbumContributorsSection';
import AlbumDetailsSection from '@/Album/AlbumDetailsSection';
import AlbumEngineersSection from '@/Album/AlbumEngineersSection';
import AlbumSpotifySection from '@/Album/AlbumSpotifySection';
import AlbumTidalSection from '@/Album/AlbumTidalSection';

function AlbumTitle() {
  const record = useRecordContext();
  return <span>{record ? record.Title : ''}</span>;
}

function AlbumEditToolbar(props: JSX.IntrinsicAttributes & ToolbarProps) {
  const redirect = useRedirect();
  const createPath = useCreatePath();

  const handleAddNewClick = () => {
    redirect(
      createPath({
        resource: 'albums',
        type: 'create',
      }),
    );
  };

  return (
    <Toolbar
      {...props}
      sx={{ display: 'flex', justifyContent: 'space-between' }}
    >
      <SaveButton />
      <Button onClick={handleAddNewClick} color='primary'>
        + Add New Album
      </Button>
    </Toolbar>
  );
}

function AlbumEdit(props: JSX.IntrinsicAttributes & EditProps<any, Error>) {
  type RecordValue = Record<string, any>;

  const transform = (data: any) => {
    const transformed = { ...data };

    // Remove empty release date
    if (!transformed.releaseDate) {
      delete transformed.releaseDate;
    }

    // Clean contributors
    if (Array.isArray(transformed.DiscographyAlbumContributors)) {
      transformed.DiscographyAlbumContributors = transformed.DiscographyAlbumContributors.filter(
        (c: RecordValue) => c.DiscographyContributorIdentifier,
      ).map((c: RecordValue) => cleanNewRecord(c, 'DiscographyAlbumContributorIdentifier'));
    }

    // Clean artists
    if (Array.isArray(transformed.DiscographyAlbumArtists)) {
      transformed.DiscographyAlbumArtists = transformed.DiscographyAlbumArtists.filter(
        (a: RecordValue) => a.DiscographyArtistIdentifier,
      ).map((a: RecordValue) => cleanNewRecord(a, 'DiscographyAlbumArtistIdentifier'));
    }

    // Clean engineers
    if (Array.isArray(transformed.DiscographyAlbumEngs)) {
      transformed.DiscographyAlbumEngs = transformed.DiscographyAlbumEngs.map(
        (eng: RecordValue) => {
          const cleaned = cleanNewRecord(eng, 'DiscographyAlbumEngIdentifier');

          // Clean nested awards for new engineers
          if (
            !eng.DiscographyAlbumEngIdentifier
            && Array.isArray(cleaned.DiscographyAlbumEngAwards)
          ) {
            cleaned.DiscographyAlbumEngAwards = cleaned.DiscographyAlbumEngAwards.map((award: RecordValue) => {
              if (award.DiscographyAlbumEngAwardIdentifier) return award;
              const awardCopy = { ...award };
              delete awardCopy.DiscographyAlbumEngAwardIdentifier;
              return awardCopy;
            });
          }

          return cleaned;
        },
      );
    }

    return transformed;
  };

  return (
    <Edit
      {...props}
      title={<AlbumTitle />}
      transform={transform}
      mutationMode='pessimistic'
      redirect={false}
    >
      <SimpleForm toolbar={<AlbumEditToolbar />}>
        <Box
          mb={3}
          width={{ xs: '100%', md: '70%' }}
          sx={{ textAlign: 'center' }}
        >
          <ImageField
            source='CoverImageUrl'
            label=''
            sx={{
              'display': 'inline-block',
              '& .RaImageField-image': {
                width: 200,
                height: 200,
                objectFit: 'cover',
                borderRadius: 2,
                display: 'block',
              },
            }}
          />
        </Box>
        <ClearError />
        <AlbumSpotifySection />
        <AlbumTidalSection />
        <AlbumDetailsSection />
        <AlbumArtistSection />
        <AlbumContributorsSection />
        <AlbumEngineersSection />
      </SimpleForm>
    </Edit>
  );
}

export default AlbumEdit;
